<template>
  <div>
    <b-alert
      v-if="partialError"
      variant="warning"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ partialError }}
      </div>
    </b-alert>
    <b-alert
      v-if="loadError"
      variant="danger"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ loadError }}
      </div>
    </b-alert>

    <b-card class="mb-1">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ช่วงเวลา"
            label-for="preset"
          >
            <b-form-select
              id="preset"
              v-model="preset"
              :options="presetOptions"
              :disabled="isLoading"
              @change="onPresetChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ตั้งแต่"
            label-for="from"
          >
            <b-form-input
              id="from"
              v-model="from"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ถึง"
            label-for="to"
          >
            <b-form-input
              id="to"
              v-model="to"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ค้นหาห้อง (Property ID)"
            label-for="pid"
          >
            <b-form-input
              id="pid"
              v-model="propertyInput"
              type="number"
              placeholder="เช่น 87"
              :disabled="isLoading"
              @keyup.enter="selectProperty(propertyInput)"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ชื่อคอนโด / โครงการ"
            label-for="project"
          >
            <b-input-group>
              <b-form-input
                id="project"
                v-model="projectQuery"
                placeholder="เช่น Rhythm Asoke"
                :disabled="isLoading"
                @keyup.enter="applyProjectFilter"
              />
              <b-input-group-append v-if="projectQuery">
                <b-button
                  variant="outline-secondary"
                  :disabled="isLoading"
                  @click="clearProjectFilter"
                >
                  ล้าง
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="คลิกมาจากไหน"
            label-for="view-source"
          >
            <b-form-select
              id="view-source"
              v-model="viewSource"
              :options="viewSourceOptions"
              :disabled="isLoading"
              @change="onViewSourceChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-row>
      <!-- เลือกห้องจากรายการที่ถูกดูมากสุด -->
      <b-col md="5">
        <b-card
          :title="listCardTitle"
          class="mb-1"
        >
          <!--
            ⚠️ ต้องเรียงที่ฝั่ง server (no-local-sorting) เพราะตารางนี้แบ่งหน้าทีละ 15
            ถ้าปล่อยให้ b-table เรียงเอง จะเรียงแค่ 15 แถวที่เห็นอยู่ แล้วเข้าใจผิดว่า
            ห้องที่คลิกเยอะสุดคือห้องนี้ ทั้งที่ตัวจริงอยู่หน้าอื่น

            ⚠️ ใช้ @row-clicked + ไฮไลต์เอง ไม่ใช้ selectable/@row-selected
            b-table ยิง row-selected เองตอน items เปลี่ยน (มัน clearSelected ให้อัตโนมัติ)
            ทำให้ไฮไลต์หลุดทุกครั้งที่โหลดใหม่ และ handler ถูกเรียกด้วยค่าว่างโดยไม่ตั้งใจ
          -->
          <b-table
            class="property-table"
            :items="properties"
            :fields="propertyFields"
            :busy="isLoading || listLoading"
            :sort-by.sync="listSortBy"
            :sort-desc.sync="listSortDesc"
            :tbody-tr-class="propertyRowClass"
            no-local-sorting
            no-sort-reset
            sort-direction="desc"
            show-empty
            :empty-text="projectFilter ? 'ไม่พบโครงการที่ค้น' : 'ยังไม่มีข้อมูล'"
            responsive
            striped
            hover
            @row-clicked="onPropertyClicked"
            @sort-changed="onListSort"
          />
          <div
            v-if="showPager(propPage, propTotalPages, propHasMore)"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="hasMultiplePages(propPage, propTotalPages, propHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="propPage <= 1 || isLoading || listLoading"
              @click="goPropPage(propPage - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pageLabel(propPage, propTotalPages, propTotal) }}</span>
            <b-button
              v-if="hasMultiplePages(propPage, propTotalPages, propHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="!hasNextPage(propPage, propTotalPages, propHasMore) || isLoading || listLoading"
              @click="goPropPage(propPage + 1)"
            >
              ถัดไป
            </b-button>
          </div>
          <small class="text-muted">
            กดหัวคอลัมน์เพื่อเรียงลำดับ · คลิกที่แถวเพื่อดูว่าห้องนั้นคนเข้ามาจากไหนบ้าง
          </small>
        </b-card>
      </b-col>

      <b-col md="7">
        <!--
          การ์ดนี้ "ไม่" ถูกกรองด้วย คลิกมาจากไหน ตั้งใจให้แสดงครบทุกทางเข้า
          เพราะหน้าที่ของมันคือบอกสัดส่วน ถ้ากรองจะเหลือแถวเดียวแล้วหมดประโยชน์
          แทนที่จะตัดทิ้ง ใช้ไฮไลต์แถวที่ตรงกับตัวกรองแทน — ยังเห็นภาพรวมและรู้ว่ากรองอะไรอยู่
        -->
        <b-card
          :title="summaryCardTitle"
          class="mb-1"
        >
          <b-alert
            v-if="!selectedId"
            variant="info"
            show
          >
            <div class="alert-body">
              เลือกห้องจากตารางด้านซ้าย หรือพิมพ์ Property ID ด้านบน
            </div>
          </b-alert>

          <template v-else>
            <b-table
              :items="summary"
              :fields="summaryFields"
              :busy="isLoading || detailLoading"
              :tbody-tr-class="summaryRowClass"
              show-empty
              empty-text="ห้องนี้ยังไม่มีการเข้าชมในช่วงที่เลือก"
              responsive
              striped
            >
              <template #cell(view_source)="row">
                <b-badge :variant="sourceVariant(row.item.view_source)">
                  {{ sourceLabel(row.item.view_source) }}
                </b-badge>
              </template>
              <template #cell(lead_rate)="row">
                {{ percent(rate(row.item.leads, row.item.views)) }}
              </template>
            </b-table>
          </template>
        </b-card>
      </b-col>
    </b-row>

    <!--
      from_page บอกว่าคลิกมาจากหน้าไหน "จริง ๆ" ซึ่งละเอียดกว่า view_source
      ที่บอกแค่ประเภทของส่วนนั้น เช่น view_source=similar แต่ from_page ชี้ว่ามาจากห้องไหน
      ใช้หาว่าห้องไหนเป็นตัวป้อนคนให้ห้องนี้
    -->
    <b-card
      v-if="selectedId"
      :title="`เส้นทางแบบละเอียด — ห้อง #${selectedId}`"
    >
      <b-table
        :items="paths"
        :fields="pathFields"
        :busy="isLoading || detailLoading"
        show-empty
        :empty-text="viewSource ? `ห้องนี้ไม่มีคนเข้ามาทาง${sourceLabel(viewSource)}ในช่วงที่เลือก` : 'ไม่มีข้อมูล'"
        responsive
        striped
      >
        <template #cell(view_source)="row">
          <b-badge :variant="sourceVariant(row.item.view_source)">
            {{ sourceLabel(row.item.view_source) }}
          </b-badge>
        </template>
        <template #cell(from_page)="row">
          {{ row.item.from_page || '—' }}
        </template>
        <template #cell(search_keyword)="row">
          {{ row.item.search_keyword || '—' }}
        </template>
        <template #cell(position)="row">
          {{ row.item.position || '—' }}
        </template>
        <template #cell(last_seen)="row">
          {{ formatDate(row.item.last_seen) }}
        </template>
      </b-table>
      <div
        v-if="showPager(pathPage, pathTotalPages, pathHasMore)"
        class="d-flex justify-content-center align-items-center"
      >
        <b-button
          v-if="hasMultiplePages(pathPage, pathTotalPages, pathHasMore)"
          size="sm"
          variant="outline-secondary"
          :disabled="pathPage <= 1 || isLoading || detailLoading"
          @click="goPathPage(pathPage - 1)"
        >
          ก่อนหน้า
        </b-button>
        <span class="mx-1 text-muted">{{ pageLabel(pathPage, pathTotalPages, pathTotal) }}</span>
        <b-button
          v-if="hasMultiplePages(pathPage, pathTotalPages, pathHasMore)"
          size="sm"
          variant="outline-secondary"
          :disabled="!hasNextPage(pathPage, pathTotalPages, pathHasMore) || isLoading || detailLoading"
          @click="goPathPage(pathPage + 1)"
        >
          ถัดไป
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
/*
 * ห้องนี้คนเข้ามาจากไหนบ้าง
 *
 * อ่านจาก property_view ที่แนบ view_source / from_page / search_keyword / source_position
 * ซึ่งการ์ดห้องบันทึกไว้ตอนถูกคลิก
 *
 * ⚠️ ทำไมต้องบันทึกเอง ไม่ใช้ document.referrer
 * ลิงก์การ์ดใช้ rel="noopener noreferrer" ซึ่ง noreferrer สั่งเบราว์เซอร์ตัด referrer ทิ้ง
 * หน้าปลายทางจึงไม่มีทางรู้ว่ามาจากไหนเลย
 */
import {
  BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
  BButton, BRow, BCol, BAlert, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import {
  analyticsHttp, apiToken, settleAll, rowsOf, loadErrorMessage, partialErrorMessage,
  pageLabel, hasNextPage, showPager, hasMultiplePages,
} from './analyticsApi'

const iso = d => d.toISOString().slice(0, 10)

const SOURCE_LABEL = {
  search_result: 'ผลการค้นหา',
  recommended: 'ห้องแนะนำ',
  similar: 'ห้องใกล้เคียง',
  available_units: 'ห้องว่างในโครงการ',
  recently_viewed: 'ดูล่าสุด',
  favorite: 'รายการโปรด',
  enquiry: 'หน้าสอบถาม',
  direct: 'เข้าตรง',
  other: 'อื่น ๆ',
}

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BAlert,
    BInputGroup,
    BInputGroupAppend,
  },
  data() {
    const today = new Date()
    /* 7 วันล่าสุดนับรวมวันนี้ จึงลบ 6 (เปลี่ยนจาก 30 วันเมื่อ 21 ก.ย. 2026) */
    const past = new Date(today.getTime() - 6 * 86400000)

    return {
      properties: [],
      summary: [],
      paths: [],
      selectedId: null,
      propertyInput: '',
      propPage: 1,
      propHasMore: false,
      propTotal: null,
      propTotalPages: null,
      listLoading: false,
      detailLoading: false,
      // projectQuery = ค่าที่กำลังพิมพ์, projectFilter = ค่าที่ส่งไป API แล้ว
      // แยกกันเพื่อให้ debounce ได้ และให้หัวข้อ/ข้อความ empty อ้างค่าที่ใช้จริง
      projectQuery: '',
      projectFilter: '',
      projectTimer: null,
      listSortBy: 'views',
      listSortDesc: true,
      viewSource: '',
      /*
       * ตัวเลือกมาจากรายงาน view_sources ไม่ใช่ลิสต์ตายตัวในโค้ด
       * -> เห็นแค่ทางเข้าที่มีข้อมูลจริงในช่วงเวลานั้น พร้อมจำนวนคลิก
       *    และถ้า frontend เพิ่มทางเข้าใหม่ ตัวเลือกก็โผล่เองโดยไม่ต้องแก้หน้านี้
       */
      viewSourceOptions: [{ value: '', text: 'ทุกทางเข้า' }],
      pathPage: 1,
      pathHasMore: false,
      pathTotal: null,
      pathTotalPages: null,
      isLoading: false,
      loadError: '',
      partialError: '',
      preset: 7,
      from: iso(past),
      to: iso(today),
      presetOptions: [
        { value: 7, text: '7 วันล่าสุด' },
        { value: 30, text: '30 วันล่าสุด' },
        { value: 90, text: '90 วันล่าสุด' },
        { value: 0, text: 'กำหนดเอง' },
      ],
      /*
       * sortable เฉพาะคอลัมน์ที่ server เรียงให้ได้
       * (ตรงกับ whitelist TOP_PROPERTY_SORT_COLUMNS ใน analytics/models/report.model.js)
       */
      propertyFields: [
        { key: 'property_id', label: 'ID' },
        { key: 'project', label: 'โครงการ', sortable: true },
        {
          key: 'views', label: 'เปิดดู', class: 'text-center', sortable: true,
        },
      ],
      summaryFields: [
        { key: 'view_source', label: 'ทางเข้า' },
        { key: 'views', label: 'เปิดดู', class: 'text-center' },
        { key: 'leads', label: 'ติดต่อ', class: 'text-center' },
        { key: 'lead_rate', label: 'ติดต่อ/ดู', class: 'text-center' },
      ],
      pathFields: [
        { key: 'view_source', label: 'ทางเข้า' },
        { key: 'from_page', label: 'คลิกมาจากหน้า' },
        { key: 'search_keyword', label: 'คำค้น' },
        { key: 'position', label: 'อันดับ', class: 'text-center' },
        { key: 'views', label: 'เปิดดู', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
        { key: 'leads', label: 'ติดต่อ', class: 'text-center' },
        { key: 'last_seen', label: 'ล่าสุด' },
      ],
    }
  },
  computed: {
    listCardTitle() {
      return this.projectFilter ? `เลือกห้อง — ${this.projectFilter}` : 'เลือกห้อง'
    },
    listParams() {
      return {
        page: this.propPage,
        per_page: 15,
        ...(this.projectFilter ? { project: this.projectFilter } : {}),
        ...(this.viewSource ? { view_source: this.viewSource } : {}),
        ...(this.listSortBy
          ? { sort: this.listSortBy, sort_dir: this.listSortDesc ? 'desc' : 'asc' }
          : {}),
      }
    },
    summaryCardTitle() {
      const base = this.selectedId ? `สรุปทางเข้า — ห้อง #${this.selectedId}` : 'สรุปทางเข้า'

      return this.viewSource ? `${base} (แสดงครบทุกทางเข้า)` : base
    },
  },
  watch: {
    projectQuery() {
      this.onProjectInput()
    },
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    clearTimeout(this.projectTimer)
  },
  methods: {
    pageLabel,
    hasNextPage,
    showPager,
    hasMultiplePages,

    /*
     * ตัวเรียก report ที่ทุกเมธอดใช้ร่วมกัน
     * เดิมนิยาม get() ไว้ข้างใน fetchData ทำให้อยากรีเฟรชตารางเดียวก็ต้องดึงทั้งหน้า
     */
    reportGet(type, extra = {}) {
      return analyticsHttp.get('analytics/v1/report', {
        params: {
          // ต่อเวลาท้ายวันเอง ไม่งั้น MySQL อ่าน to เป็นเที่ยงคืนแล้วข้อมูลทั้งวันนั้นหลุด
          from: `${this.from} 00:00:00`,
          to: `${this.to} 23:59:59`,
          type,
          ...extra,
        },
        headers: { Authorization: `Bearer ${apiToken()}` },
      })
    },

    /* โหลดทั้งหน้า — ใช้ตอนเปิดหน้าและตอนเปลี่ยนช่วงเวลาเท่านั้น */
    async fetchData() {
      if (!this.requireToken()) return

      try {
        this.isLoading = true
        this.loadError = ''
        this.partialError = ''

        await Promise.all([this.loadList(), this.loadDetail(), this.loadViewSources()])
      } catch (error) {
        this.properties = []
        this.summary = []
        this.paths = []
        this.loadError = loadErrorMessage(error)
        console.error('Error loading property journey:', error)
      } finally {
        this.isLoading = false
      }
    },

    /*
     * รีเฟรชเฉพาะตารางรายชื่อห้อง (กรอง / เรียง / เปลี่ยนหน้า)
     * ไม่แตะฝั่งรายละเอียด ห้องที่เลือกไว้จึงยังอยู่
     */
    async fetchList() {
      if (!this.requireToken()) return

      try {
        this.listLoading = true
        await this.loadList()
      } catch (error) {
        this.properties = []
        this.loadError = loadErrorMessage(error)
        console.error('Error loading property list:', error)
      } finally {
        this.listLoading = false
      }
    },

    /*
     * รีเฟรชเฉพาะฝั่งรายละเอียด (เลือกห้อง / เปลี่ยนหน้าเส้นทาง)
     * ไม่ดึงตารางรายชื่อใหม่ ลำดับ/หน้า/คำค้นที่ตั้งไว้จึงอยู่กับที่
     */
    async fetchDetail() {
      if (!this.requireToken()) return

      try {
        this.detailLoading = true
        await this.loadDetail()
      } finally {
        this.detailLoading = false
      }
    },

    requireToken() {
      if (apiToken()) return true

      this.loadError = 'ไม่พบ token ของ API ใน userData — กรุณาออกจากระบบแล้วเข้าใหม่'

      return false
    },

    /*
     * ตัวเลือกของ dropdown "คลิกมาจากไหน"
     *
     * ผูกกับช่วงเวลาด้วย จึงต้องโหลดใหม่ทุกครั้งที่เปลี่ยนวันที่ ไม่ใช่โหลดครั้งเดียวตอนเปิดหน้า
     * ถ้าตัวที่เลือกไว้ไม่มีข้อมูลในช่วงใหม่ ให้กลับไปเป็น "ทุกทางเข้า" ไม่งั้นตารางจะว่าง
     * โดยที่ dropdown ยังโชว์ค่าที่เลือกอยู่ ซึ่งดูเหมือนระบบพัง
     */
    async loadViewSources() {
      const r = await settleAll({ sources: this.reportGet('view_sources', { per_page: 50 }) })
      const rows = rowsOf(r.sources)

      this.viewSourceOptions = [
        { value: '', text: 'ทุกทางเข้า' },
        ...rows.map(row => ({
          value: row.view_source,
          text: `${this.sourceLabel(row.view_source)} (${row.views})`,
        })),
      ]

      if (this.viewSource && !rows.some(row => row.view_source === this.viewSource)) {
        this.viewSource = ''
      }
    },

    async loadList() {
      const r = await settleAll({ list: this.reportGet('top_properties', this.listParams) })

      if (!r.list.ok) throw r.list.reason

      this.properties = rowsOf(r.list)
      this.propHasMore = r.list.hasMore
      this.propTotal = r.list.total
      this.propTotalPages = r.list.totalPages
    },

    async loadDetail() {
      if (!this.selectedId) {
        this.summary = []
        this.paths = []
        this.pathHasMore = false
        this.pathTotal = null
        this.pathTotalPages = null

        return
      }

      /*
       * settleAll อ้างอิงด้วยชื่อ ไม่ใช่ตำแหน่ง — เพิ่ม/ลบ/สลับ request ตรงไหน
       * ผลลัพธ์ก็ไม่เลื่อนไปเข้าตารางผิดตัว และตัวไหนพังก็ไม่ล้มทั้งหน้า
       */
      const r = await settleAll({
        summary: this.reportGet('view_source_by_property', {
          property_id: this.selectedId, per_page: 50,
        }),
        /*
         * paths ถูกกรองด้วย view_source แต่ summary ไม่ถูกกรอง (ดูหมายเหตุที่การ์ดสรุป)
         */
        paths: this.reportGet('property_entry_paths', {
          property_id: this.selectedId,
          page: this.pathPage,
          per_page: 20,
          ...(this.viewSource ? { view_source: this.viewSource } : {}),
        }),
      })

      this.summary = rowsOf(r.summary)
      this.paths = rowsOf(r.paths)
      this.pathHasMore = !!r.paths.hasMore
      this.pathTotal = r.paths.total
      this.pathTotalPages = r.paths.totalPages

      this.partialError = partialErrorMessage(r, {
        summary: 'สรุปทางเข้า',
        paths: 'เส้นทางแบบละเอียด',
      })
    },
    selectProperty(id) {
      const n = Number.parseInt(id, 10)

      if (!Number.isFinite(n)) return

      this.selectedId = n
      this.pathPage = 1
      // ดึงเฉพาะฝั่งรายละเอียด ตารางรายชื่อห้องไม่ต้องโหลดใหม่
      this.fetchDetail()
    },

    /*
     * @row-clicked ยิงเฉพาะตอนคนกดจริง ต่างจาก @row-selected ที่ b-table ยิงเองด้วย
     * ตอนมันเคลียร์ selection ให้อัตโนมัติเมื่อ items เปลี่ยน
     */
    onPropertyClicked(item) {
      const id = item?.property_id

      if (id === undefined || id === null) return

      this.propertyInput = String(id)
      this.selectProperty(id)
    },

    /* ไฮไลต์ทางเข้าที่กำลังกรองอยู่ ในการ์ดสรุปที่ยังแสดงครบทุกทางเข้า */
    summaryRowClass(item) {
      return item && this.viewSource && item.view_source === this.viewSource
        ? 'table-active font-weight-bold'
        : ''
    },

    /* ไฮไลต์แถวเอง — ผูกกับ selectedId ไม่ใช่ selection ภายในของ b-table
     * ที่หายไปทุกครั้งที่ items เปลี่ยน */
    propertyRowClass(item) {
      return item && this.selectedId && Number(item.property_id) === Number(this.selectedId)
        ? 'table-active'
        : ''
    },

    /*
     * เปลี่ยนทางเข้า กระทบทั้งตารางรายชื่อ (ยอดคลิกนับแค่ทางนั้น) และตารางเส้นทาง
     * แต่ไม่กระทบการ์ดสรุป — ตัวนั้นตั้งใจให้แสดงครบทุกทางเข้าเสมอ
     */
    onViewSourceChange() {
      this.propPage = 1
      this.pathPage = 1

      return Promise.all([this.fetchList(), this.fetchDetail()])
    },

    /*
     * .sync อัปเดต listSortBy/listSortDesc ให้ก่อน event นี้จะยิงแล้ว
     * ต้องกลับหน้า 1 เสมอ เพราะเปลี่ยนลำดับแล้วหน้าเดิมไม่ได้แปลว่าอะไรอีกต่อไป
     */
    onListSort() {
      this.propPage = 1
      this.fetchList()
    },

    /*
     * พิมพ์ชื่อโครงการแล้วรอ 400ms ค่อยยิง — ไม่งั้นยิง request ทุกตัวอักษร
     * (@keyup.enter เรียก applyProjectFilter ตรง ๆ สำหรับคนที่ไม่อยากรอ)
     */
    onProjectInput() {
      clearTimeout(this.projectTimer)
      this.projectTimer = setTimeout(this.applyProjectFilter, 400)
    },
    applyProjectFilter() {
      clearTimeout(this.projectTimer)

      const next = this.projectQuery.trim()

      if (next === this.projectFilter) return

      this.projectFilter = next
      this.propPage = 1
      this.fetchList()
    },
    clearProjectFilter() {
      this.projectQuery = ''
      this.applyProjectFilter()
    },
    goPropPage(page) {
      this.propPage = Math.max(1, page)
      this.fetchList()
    },
    goPathPage(page) {
      this.pathPage = Math.max(1, page)
      this.fetchDetail()
    },
    onPresetChange() {
      if (!this.preset) return

      const today = new Date()

      this.to = iso(today)
      this.from = iso(new Date(today.getTime() - (this.preset - 1) * 86400000))
      this.propPage = 1
      this.pathPage = 1
      this.fetchData()
    },
    onDateChange() {
      this.preset = 0
      this.propPage = 1
      this.pathPage = 1
      this.fetchData()
    },
    sourceLabel(src) {
      return SOURCE_LABEL[src] || src || '-'
    },
    sourceVariant(src) {
      if (src === 'search_result') return 'light-primary'
      if (src === 'direct') return 'light-secondary'

      return 'light-info'
    },
    rate(part, whole) {
      const w = Number(whole || 0)

      return w ? (100 * Number(part || 0)) / w : null
    },
    percent(value) {
      const n = Number(value)

      return Number.isFinite(n) ? `${n.toFixed(1)}%` : '—'
    },
    formatDate(value) {
      if (!value) return '—'

      const d = new Date(value)

      return Number.isNaN(d.getTime()) ? value : d.toLocaleString('th-TH')
    },
  },
}
</script>

<style scoped>
/* URL ยาวในคอลัมน์ "คลิกมาจากหน้า" ดันตารางกว้างเกินจำเป็นบนมือถือ */
:deep(.table td) {
  word-break: break-word;
}

/*
 * แถวที่คลิกได้ — มือถือไม่มี hover ให้เห็น
 * ผูกกับ .property-table ไม่ใช่ .b-table-selectable เพราะเลิกใช้ prop selectable แล้ว
 */
:deep(.property-table tbody tr) {
  cursor: pointer;
}
</style>
